import { DeveloperTokenManager } from '../utils/developerTokenManager';

export const BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://api.cluva.io"
    : "https://api.cluva.in");

export const API_URLS = {
  // Authentication endpoints
  SIGNUP_REQUEST: `${BASE_URL}/auth/signup/request`,
  SIGNUP_VERIFY_REQUEST: `${BASE_URL}/auth/signup/verify`,
  LOGIN_REQUEST: `${BASE_URL}/auth/signin/request`,
  SIGNIN_VERIFY_REQUEST: `${BASE_URL}/auth/signin/verify`,
  REFRESH_TOKEN: `${BASE_URL}/auth/token/refresh`,
  GOOGLE_OAUTH_REQUEST: `${BASE_URL}/auth/google/`,
  CHECK_SUBDOMAIN: (subdomain: string) => `${BASE_URL}/auth/check_subdomain/?subdomain=${subdomain}`,
  ONBOARDING: `${BASE_URL}/auth/onboarding`,
  LOGOUT: `${BASE_URL}/auth/logout`,
  GET_USER_PROFILE: `${BASE_URL}/auth/user`,
  GET_WORKSPACES: `${BASE_URL}/auth/workspaces`,
  GET_ICON: (workspaceId: number) => `${BASE_URL}/auth/icon/${workspaceId}`,
  UPDATE_PROFILE_AND_WORKSPACE_URL: `${BASE_URL}/feedback/user_update`,

  UPDATE_APP_TOUR_GUIDE_STATUS: `${BASE_URL}/auth/tour_guide/done`,
  GET_APP_TOUR_GUIDE_STATUS: `${BASE_URL}/auth/tour_guide/status`,

  // Feedback and Card endpoints
  CREATE_CARD_BASE: `${BASE_URL}/feedback/card/create`,
  CREATE_FEEDBACK_INSIDE_CARD: (cardId: number, workspaceId: number) =>
    `${BASE_URL}/feedback/card/${cardId}?workspace_id=${workspaceId}`,
  PUT_UPDATE_FEEDBACK_BY_FEEDBACK_ID: (feedbackId: number) =>
    `${BASE_URL}/feedback/update/${feedbackId}`,
  GET_DRAFT: (workspaceId: number) =>
    `${BASE_URL}/feedback/drafts?workspace_id=${workspaceId}`,
  GET_CREATED_FEEDBACK: (workspaceId: number) =>
    `${BASE_URL}/feedback/sent?workspace_id=${workspaceId}`,
  DELETE_CARD: (cardId: number) => `${BASE_URL}/feedback/delete_card/${cardId}`,

  MARKED_FEEDBACK_VIEW_BY_DEVELOPER: (cardId: number) =>
    `${BASE_URL}/feedback/mark_card_viewed?card_id=${cardId}`,

  // Upload endpoints
  UPLOAD_MESSAGE: `${BASE_URL}/feedback/message`,
  UPLOAD_FILE: `${BASE_URL}/feedback/upload`,
  UPLOAD_FILE_VOICE: `${BASE_URL}/feedback/upload/voice`,
  GET_FILE_VOICE: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/get/voice?feedback_id=${feedbackId}`,
  DELETE_FILE_VOICE: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/delete/voice?feedback_id=${feedbackId}`,
  DELETE_FILE: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/${feedbackId}`,

  UPLOAD_RESHARE_FEEDBACK: `${BASE_URL}/feedback/reshare_feedback/`,

  // Sharing endpoints
  SHARE_FEEDBACK_WITH_DEVELOPER_BY_CARD_ID: (cardId: number) =>
    `${BASE_URL}/feedback/share/card/${cardId}`,
  VERIFY_DEVELOPER_TOKEN: `${BASE_URL}/feedback/exchange_share_code`,
  VIEW_FEEDBACK_BY_DEVELOPER: `${BASE_URL}/feedback/developer/feedbacks`,
  GET_DEVELOPER_URL: (developer_email: string) => `${BASE_URL}/feedback/get_shared_url?developer_email=${developer_email}`,
  GET_DEVELOPER_CARD_SENDERS_LIST: (cardId: number) => `${BASE_URL}/feedback/list/${cardId}`,

  FEEDBACK_DEVELOPER_STATUS_UPDATE: (feedbackId: number) =>
    `${BASE_URL}/feedback/developer_action/${feedbackId}`,
  GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE: (cardId: number) =>
    `${BASE_URL}/feedback/card_feedback_status/${cardId}`,
  GET_DEVELOPER_ACTION_STATUS: (feedbackId: number) =>
    `${BASE_URL}/feedback/developer_action_status/${feedbackId}`,

  // Video thumbnail endpoints
  UPLOAD_VIDEO_THUMBNAIL: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/thumbnail/${feedbackId}`,
  GET_VIDEO_THUMBNAIL: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/get_thumbnail/${feedbackId}`,

  GET_SCREENSHOT: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/files/${feedbackId}`,

  // Notification endpoints
  GET_NOTIFICATIONS: `${BASE_URL}/feedback/notifications`,
  MARK_NOTIFICATIONS_AS_READ: (notification_id: number) => `${BASE_URL}/feedback/notifications/${notification_id}`,

  // Environment / technical context
  FEEDBACK_ENVIRONMENT: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/environment/${feedbackId}`,

  // Jira Integration
  CHECK_JIRA_BUTTON_STATUS: (feedbackId: string | number) => `${BASE_URL}/feedback/jira/status/${feedbackId}/`,

  // Iframe pre-flight check
  CHECK_IFRAME_HEADERS: `${BASE_URL}/auth/check_iframe_headers`,

  // Cross-subdomain token transfer
  REQUEST_TRANSFER_CODE: `${BASE_URL}/auth/transfer_code`,
  VERIFY_TRANSFER_CODE: `${BASE_URL}/auth/exchange_code`,
};

// Interface for the draft API response
export interface DraftApiResponse {
  id: number;
  name: string;
  workspace_id: number;
  card_id: number;
  created_by: number;
  url: string;
  screenshot_url: string | null;
  recording_url: string | null;
  voice_recording_url: string | null;
  message: string;
  status: "draft";
  created_at: string;
  collaborators: string[];
}

// Single feedback item inside a shared card group
export interface SharedFeedbackApiResponse {
  id: number;
  name: string;
  workspace_id: number;
  card_id: number;
  created_by: number;
  url: string;
  screenshot_url: string | null;
  recording_url: string | null;
  voice_recording_url: string | null;
  message: string | null;
  status: "sent" | "draft";
  updated_status: "invited" | "viewed" | "in_progress" | "done" | null;
  created_at: string;
}

// Card group as returned by GET_CREATED_FEEDBACK
export interface SharedFeedbackCardGroupApiResponse {
  card_id: number;
  card_name: string;
  preview_screenshot: string | null;
  feedbacks: Array<{
    feedback: SharedFeedbackApiResponse;
    sent_at: string;
  }>;
}

export interface MarkCardViewedResponse {
  success: boolean;
  message: string;
  updated_records: number;
}

// Interface for developer feedback API response
export interface DeveloperFeedbackApiResponse {
  card_id: number;
  card_name: string;
  feedbacks: Array<{
    id: number;
    screenshot_url: string | null;
    recording_url: string | null;
    voice_recording_url: string | null;
    message: string | null;
    created_at: string;
    sent_at: string | null;
    profile_image: string | null;
    creator_name: string | null;
  }>;
}

// Interface for transformed draft card data
export interface TransformedDraftCard {
  id: number;
  name: string;
  created_at: string;
  imageUrl: string;
  feedbackCount: number;
  feedbackData: {
    name: string;
    collaborators: string[];
    accessType: "comment" | "edit";
  };
  status: "draft";
  savedFeedbackItems?: any[];
  savedWorkspaceState?: any;
  firstScreenshotImage?: string | undefined;
  originalApiData?: DraftApiResponse;
}

// Interface for transformed shared feedback card data
export interface TransformedSharedFeedbackCard {
  id: number;
  name: string;
  created_at: string;
  imageUrl: string;
  feedbackCount: number;
  status: "Invited" | "Viewed" | "In progress" | "Done";
  sharedWith: string[];
  sharedAt: string;
  daysAgo: string;
}

// Interface for transformed developer feedback card data
export interface TransformedDeveloperFeedbackCard {
  id: number;
  name: string;
  created_at: string;
  imageUrl: string;
  feedbackCount: number;
  status: "Shared with me";
  sharedAt: string;
  daysAgo: string;
  originalApiData?: DeveloperFeedbackApiResponse;
}

// Tour guide key constants
export const TOUR_GUIDE_KEYS = {
  APP_TOUR: 1,
  SCREEN_SHARE_INSTRUCTION: 2,
  BETA_POPUP: 3,
} as const;

export type TourGuideKey = typeof TOUR_GUIDE_KEYS[keyof typeof TOUR_GUIDE_KEYS];

export const getAccessToken = (): string | null => {
  try {
    return (
      localStorage.getItem("access_token") ||
      (window as any).__feebdack_tokens?.access_token ||
      null
    );
  } catch (error) {
    console.warn("Could not retrieve access token:", error);
    return (window as any).__feebdack_tokens?.access_token || null;
  }
};

export const markTourGuideDone = async (guideKey: TourGuideKey): Promise<boolean> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return false;
    const response = await fetch(API_URLS.UPDATE_APP_TOUR_GUIDE_STATUS, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ guide_key: guideKey }),
    });
    return response.ok;
  } catch (error) {
    console.error('Error marking tour guide as done:', error);
    return false;
  }
};

export const getTourGuideStatus = async (): Promise<string[]> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return [];
    const response = await fetch(API_URLS.GET_APP_TOUR_GUIDE_STATUS, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) return [];
    const data = await response.json();
    return (data.completed_guides ?? []).map((g: { guide_key: string }) => String(g.guide_key));
  } catch (error) {
    console.error('Error fetching tour guide status:', error);
    return [];
  }
};

const generatePlaceholderImage = (feedbackName: string): string =>
  `https://via.placeholder.com/400x300/6B87E6/FFFFFF?text=${encodeURIComponent(
    feedbackName.substring(0, 20) + (feedbackName.length > 20 ? "..." : "")
  )}`;

const getFullImageUrl = (imagePath: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http")) return imagePath;
  return `${BASE_URL}${imagePath}`;
};

const calculateDaysAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  if (diffMinutes < 5) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
};

export const fetchDraftCards = async (workspaceId: number): Promise<{ success: boolean; data?: TransformedDraftCard[]; error?: string }> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return { success: false, error: "No access token available" };
    const response = await fetch(API_URLS.GET_DRAFT(workspaceId), {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    if (!response.ok) return { success: false, error: `HTTP error! status: ${response.status}` };
    const responseData: any = await response.json();
    let draftData: DraftApiResponse[] = [];
    if (Array.isArray(responseData)) {
      draftData = responseData;
    } else if (responseData && typeof responseData === "object") {
      const groupKeys = Object.keys(responseData);
      for (const key of groupKeys) {
        if (Array.isArray(responseData[key])) draftData.push(...responseData[key]);
      }
    }
    const cardMap = new Map<number, DraftApiResponse>();
    draftData.forEach((item) => {
      const cardId = item.card_id || item.id;
      if (cardId) {
        const existing = cardMap.get(cardId);
        if (!existing || new Date(item.created_at) > new Date(existing.created_at)) {
          cardMap.set(cardId, item);
        }
      }
    });
    const transformedDrafts: TransformedDraftCard[] = Array.from(cardMap.values()).map((item) => {
      const screenshotUrl = getFullImageUrl(item.screenshot_url);
      return {
        id: item.card_id,
        name: item.name,
        created_at: item.created_at,
        imageUrl: screenshotUrl || generatePlaceholderImage(item.name),
        feedbackCount: 1,
        feedbackData: { name: item.name, collaborators: item.collaborators || [], accessType: "comment" as const },
        status: "draft" as const,
        savedFeedbackItems: [],
        savedWorkspaceState: { screenshots: [], recordings: [], feedbackItemsOrder: [], url: item.url, showIframe: true },
        firstScreenshotImage: screenshotUrl || undefined,
        originalApiData: item,
      };
    });
    return { success: true, data: transformedDrafts };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
  }
};

const STATUS_PRIORITY: Record<string, number> = { done: 4, in_progress: 3, viewed: 2, invited: 1 };

export const fetchSharedFeedbackCards = async (workspaceId: number): Promise<{ success: boolean; data?: TransformedSharedFeedbackCard[]; error?: string }> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return { success: false, error: "No access token available" };
    const response = await fetch(API_URLS.GET_CREATED_FEEDBACK(workspaceId), {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    if (!response.ok) return { success: false, error: `HTTP error! status: ${response.status}` };
    const responseData: Record<string, SharedFeedbackCardGroupApiResponse> = await response.json();

    const transformedShared: TransformedSharedFeedbackCard[] = Object.values(responseData).map((card) => {
      // Determine the highest-priority status across all feedback items in the card
      let topRawStatus: "invited" | "viewed" | "in_progress" | "done" = "invited";
      let latestSentAt = "";
      let earliestCreatedAt = "";

      for (const entry of card.feedbacks) {
        const s = entry.feedback.updated_status;
        if (s && (STATUS_PRIORITY[s] ?? 0) > (STATUS_PRIORITY[topRawStatus] ?? 0)) {
          topRawStatus = s;
        }
        if (!latestSentAt || new Date(entry.sent_at) > new Date(latestSentAt)) {
          latestSentAt = entry.sent_at;
        }
        if (!earliestCreatedAt || new Date(entry.feedback.created_at) < new Date(earliestCreatedAt)) {
          earliestCreatedAt = entry.feedback.created_at;
        }
      }

      const statusMap: Record<string, "Invited" | "Viewed" | "In progress" | "Done"> = {
        invited: "Invited",
        viewed: "Viewed",
        in_progress: "In progress",
        done: "Done",
      };
      const status = statusMap[topRawStatus] ?? "Invited";
      const sharedAt = latestSentAt || earliestCreatedAt;

      return {
        id: card.card_id,
        name: card.card_name,
        created_at: earliestCreatedAt,
        imageUrl: card.preview_screenshot || generatePlaceholderImage(card.card_name),
        feedbackCount: card.feedbacks.length,
        status,
        sharedWith: [],
        sharedAt,
        daysAgo: calculateDaysAgo(sharedAt),
      };
    });

    return { success: true, data: transformedShared };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
  }
};

export const fetchDeveloperFeedbackCards = async (developerToken?: string | null, developerEmail?: string | null): Promise<{ success: boolean; data?: TransformedDeveloperFeedbackCard[]; error?: string }> => {
  try {
    const tokenToUse = developerToken;
    if (!tokenToUse) return { success: false, error: "No developer access token available" };
    const headers: HeadersInit = { "Content-Type": "application/json", Authorization: `Bearer ${tokenToUse}` };
    if (developerEmail) headers['X-Developer-Email'] = developerEmail;
    const response = await fetch(API_URLS.VIEW_FEEDBACK_BY_DEVELOPER, { method: "GET", headers });
    if (!response.ok) {
      const errorText = await response.text();
      if (response.status === 401 && developerEmail && developerToken) {
        DeveloperTokenManager.removeToken(developerEmail, developerToken);
      }
      return { success: false, error: `HTTP error! status: ${response.status}, message: ${errorText}` };
    }
    const responseData: DeveloperFeedbackApiResponse[] = await response.json();
    const transformedDeveloperCards: TransformedDeveloperFeedbackCard[] = responseData.map((item) => {
      const { card_id, card_name, feedbacks } = item;
      const firstScreenshot = feedbacks.find(f => f.screenshot_url)?.screenshot_url;
      const cardImageUrl = firstScreenshot ? getFullImageUrl(firstScreenshot) || generatePlaceholderImage(card_name) : generatePlaceholderImage(card_name);
      const actualFeedbackCount = feedbacks.filter(f => f.message || f.screenshot_url || f.recording_url || f.voice_recording_url).length;
      const sortedFeedbacks = feedbacks.sort((a, b) => new Date(b.sent_at || b.created_at).getTime() - new Date(a.sent_at || a.created_at).getTime());
      const latestFeedback = sortedFeedbacks[0];
      const sharedAt = latestFeedback?.sent_at || latestFeedback?.created_at;
      return {
        id: card_id,
        name: card_name,
        created_at: latestFeedback?.created_at || new Date().toISOString(),
        imageUrl: cardImageUrl,
        feedbackCount: actualFeedbackCount,
        status: "Shared with me" as const,
        sharedAt,
        daysAgo: calculateDaysAgo(sharedAt),
        originalApiData: item,
      };
    });
    return { success: true, data: transformedDeveloperCards };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
  }
};

export const getDraftFeedbackItems = async (draftId: number): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return { success: false, error: "No access token available" };
    return { success: true, data: {} };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
  }
};

export const updateDraftStatus = async (draftId: number, status: string, workspaceId?: number): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return { success: false, error: "No access token available" };
    const response = await fetch(API_URLS.PUT_UPDATE_FEEDBACK_BY_FEEDBACK_ID(draftId), {
      method: "PUT",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ status, workspace_id: workspaceId }),
    });
    if (!response.ok) return { success: false, error: `HTTP error! status: ${response.status}` };
    const result = await response.json();
    return { success: true, data: result };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
  }
};

export const checkAudioExists = async (feedbackId: string): Promise<boolean> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return false;
    const response = await fetch(API_URLS.GET_FILE_VOICE(feedbackId), {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}`, Range: "bytes=0-0" },
    });
    return response.ok || response.status === 206;
  } catch (error) {
    return false;
  }
};

export const getVideoThumbnail = async (feedbackId: string | number): Promise<string | null> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return null;
    const response = await fetch(API_URLS.GET_VIDEO_THUMBNAIL(feedbackId), {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (data.thumbnail_url) return `${BASE_URL}${data.thumbnail_url}`;
    return null;
  } catch (error) {
    return null;
  }
};

class AudioConverter {
  static async convertToMP3(inputBlob: Blob): Promise<Blob> {
    try {
      if (inputBlob.type.includes("mp3") || inputBlob.type.includes("mpeg")) return inputBlob;
      return new Blob([inputBlob], { type: "audio/mp3" });
    } catch (error) {
      return new Blob([inputBlob], { type: "audio/mp3" });
    }
  }
}

class ReshareApiService {
  static async reshareFeedback(feedbackId: string | number, message: string, screenshotFile?: Blob, voiceFile?: Blob): Promise<{ success: boolean; message: string; feedback_id: number }> {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error("No access token available");
    const cleanFeedbackId = String(feedbackId).replace(/^feedback_/, "").trim();
    const formData = new FormData();
    formData.append("feedback_id", cleanFeedbackId);
    formData.append("message", message);
    if (screenshotFile) {
      const isVideo = screenshotFile.type.includes("video");
      const fileName = isVideo ? `recording_${Date.now()}_updated.mp4` : `screenshot_${Date.now()}_updated.png`;
      const mimeType = isVideo ? "video/mp4" : "image/png";
      const processedBlob = screenshotFile.type.includes(isVideo ? "mp4" : "png") ? screenshotFile : new Blob([screenshotFile], { type: mimeType });
      formData.append("screenshot", new File([processedBlob], fileName, { type: mimeType, lastModified: Date.now() }));
    }
    if (voiceFile) {
      let processedVoiceFile = voiceFile;
      try { processedVoiceFile = await AudioConverter.convertToMP3(voiceFile); } catch { processedVoiceFile = new Blob([voiceFile], { type: "audio/mp3" }); }
      formData.append("voice_recording", new File([processedVoiceFile], `audio_${Date.now()}_updated.mp3`, { type: "audio/mp3", lastModified: Date.now() }));
    }
    const response = await fetch(API_URLS.UPLOAD_RESHARE_FEEDBACK, {
      method: "POST",
      body: formData,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try { const errorText = await response.text(); if (errorText) errorMessage = `HTTP ${response.status}: ${errorText}`; } catch {}
      throw new Error(errorMessage);
    }
    return response.json();
  }
}

export const markCardViewedByDeveloper = async (
  cardId: number,
  developerToken: string
): Promise<{ success: boolean; data?: MarkCardViewedResponse; error?: string }> => {
  try {
    const response = await fetch(API_URLS.MARKED_FEEDBACK_VIEW_BY_DEVELOPER(cardId), {
      method: "POST",
      headers: { Authorization: `Bearer ${developerToken}`, "Content-Type": "application/json" },
    });
    if (!response.ok) return { success: false, error: `HTTP error! status: ${response.status}` };
    const data: MarkCardViewedResponse = await response.json();
    return { success: data.success, data };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : "Unknown error occurred" };
  }
};

export { ReshareApiService, AudioConverter };
